import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import "./CampaignWizard.css";
import ApiService from "../../services/Api.service";
import toast from "react-hot-toast";
import { campaignWizardSchema } from "./CampaignWizard.validation";
import WizardHeader from "./WizardHeader";
import WizardProgress from "./WizardProgress";
import BotTypeSelection from "./BotTypeSelection";
import BasicInfoForm from "./BasicInfoForm";
import LanguageVoiceForm from "./LanguageVoiceForm";
import ScriptForm from "./ScriptForm";
import WizardFooter from "./WizardFooter";
import { STEPS } from "./CampaignWizard.constants";


const CampaignWizard = ({ onCancel, onSave }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedBotType, setSelectedBotType] = useState("call");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [languageOptions, setLanguageOptions] = useState({});
  const [voiceCharacters, setVoiceCharacters] = useState([]);
  const [voiceCharactersLoading, setVoiceCharactersLoading] = useState(false);
  const [llmOptions, setLlmOptions] = useState([]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(campaignWizardSchema),
    defaultValues: {
      campaign_name: "",
      description: "",
      language: "Hinglish",
      bot_type: "call",
      voice_gender: "female",
      voice_character_id: "",
      initial_message: "",
      call_flow_script: "",
      timeout: 10,
      ai_model: "gpt-4",
      start_hour: "09",
      start_minute: "00",
      start_am_pm: "AM",
      end_hour: "06",
      end_minute: "00",
      end_am_pm: "PM",
    },
  });

  const watchedLanguage = watch("language");
  const watchedVoiceGender = watch("voice_gender");

  useEffect(() => {
    const fetchVoiceCharacters = async () => {
      if (!watchedLanguage || !watchedVoiceGender) {
        setVoiceCharacters([]);
        return;
      }

      setVoiceCharactersLoading(true);
      try {
        const res = await ApiService.getVoiceCharacterOptions({
          language: watchedLanguage,
          gender: watchedVoiceGender,
        });
        if (res?.status === 200) {
          setVoiceCharacters(res.data?.data || []);
        }
      } catch {
        toast.error("Failed to load voice characters");
      } finally {
        setVoiceCharactersLoading(false);
      }
    };
    fetchVoiceCharacters();
  }, [watchedLanguage, watchedVoiceGender]);

  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        const res = await ApiService.getLanguageOptions();
        if (res?.status === 200) {
          setLanguageOptions(res.data);
        }
      } catch {
        toast.error("Failed to load language options");
      }
    };
    fetchLanguageOptions();
  }, []);

  useEffect(() => {
    const fetchIvrOptions = async () => {
      try {
        const res = await ApiService.getIvrOptions({ language: watchedLanguage || "en" });
        if (res?.status === 200) {
          setLlmOptions(res.data?.llm_options || []);
        }
      } catch {
      }
    };
    if (watchedLanguage) {
      fetchIvrOptions();
    }
  }, [watchedLanguage]);

  useEffect(() => {
    if (!llmOptions?.length) return;

    const getValue = (item) =>
      typeof item === "string"
        ? item
        : item?.value || item?.id || item?.label;

    const firstValue = getValue(llmOptions[0]);
    const currentValue = getValue(control._formValues?.ai_model);

    if (!currentValue || !llmOptions.some((opt) => getValue(opt) === currentValue)) {
      setValue("ai_model", firstValue);
    }
  }, [llmOptions, setValue]);

  const handleNext = async () => {
    const isCallStepBot = watch("bot_type") === "call";

    const stepFields = {
      1: ["campaign_name"],
      2: ["language", ...(isCallStepBot ? ["voice_character_id"] : [])],
      3: ["initial_message", "call_flow_script"],
    };

    const fieldsToValidate = stepFields[currentStep];
    if (fieldsToValidate) {
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data) => {
    const formatTime = (hour, minute, ampm) => {
      let h = parseInt(hour, 10);
      if (ampm === "PM" && h !== 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${minute}`;
    };

    const payload = {
      ...data,
      bot_type: data?.bot_type || selectedBotType,
      start_time: formatTime(data.start_hour, data.start_minute, data.start_am_pm),
      end_time: formatTime(data.end_hour, data.end_minute, data.end_am_pm),
    };
    setLoading(true);
    try {
      await onSave(payload);
    } finally {
      setLoading(false);
    }
  };

  const isCallBot = watch("bot_type") === "call";


  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BotTypeSelection
            selectedBotType={selectedBotType}
            onSelect={(botId) => {
              setSelectedBotType(botId);
              setValue("bot_type", botId);
            }}
          />
        );
      case 1:
        return <BasicInfoForm control={control} errors={errors} />;
      case 2:
        return (
          <LanguageVoiceForm
            control={control}
            errors={errors}
            languageOptions={languageOptions}
            voiceCharacters={voiceCharacters}
            voiceCharactersLoading={voiceCharactersLoading}
            watchedLanguage={watchedLanguage}
            watchedVoiceGender={watchedVoiceGender}
            setValue={setValue}
            isCallBot={isCallBot}
          />
        );
      case 3:
        return <ScriptForm control={control} errors={errors} showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced} llmOptions={llmOptions} />;
      default:
        return null;
    }
  };

  const canProceed = () => {
    if (currentStep === 0) return selectedBotType !== null;
    return true;
  };

  return (
    <div className="campaign-wizard">
      {/* Header */}
      <WizardHeader selectedBotType={selectedBotType} onCancel={onCancel} />

      {/* Progress Steps */}
      <WizardProgress currentStep={currentStep} />

      {/* Step Content */}
      <div className="wizard-body">
        <form onSubmit={handleSubmit(onSubmit)}>{renderStepContent()}</form>
      </div>

      {/* Footer Actions */}
      <WizardFooter
        currentStep={currentStep}
        onBack={handleBack}
        onNext={handleNext}
        onSubmit={onSubmit}
        canProceed={canProceed}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </div>
  );
};

export default CampaignWizard;
